import { Suspense, useCallback, useEffect, useRef } from 'react'
import { shallowEqual, useSelector, useDispatch } from 'react-redux'
import { ToastContainer, toast } from 'react-toastify'
import PrivatePage from './app/PrivatePage'
import PublicPage from './app/PublicPage'
import AuthInit from './app/modules/auth/_redux/AuthInit'
import { logout } from './app/modules/auth/_redux/authSlice'
import crossTabLogout from './utils/crossTabLogout'
import { applyTheme } from './utils/theme'

import RotatingLinesLoader from './app/components/_shared/Loaders/RotatingLinesLoader'
import MaintenanceWatcher from './app/modules/maintenance/MaintenanceWatcher'
import MaintenanceOverlay from './app/modules/maintenance/MaintenanceOverlay'
import 'react-toastify/dist/ReactToastify.css'
import { useLocation, useNavigate } from 'react-router-dom'
const nonAuthRoutes = ['/login', '/forgotPassword', '/otp', '/resetpassword', '/resendEmail']
const BUILD_VERSION_ENDPOINT = '/build-version.json'
const BUILD_VERSION_MIN_CHECK_GAP_MS = 2 * 60 * 1000

const App = () => {
    const location = useLocation()
    const navigate = useNavigate()
    const dispatch = useDispatch()
    const lastLogoutEventRef = useRef(null) // Track last logout event
    
    const { isAuthorized, token, isInitializing } = useSelector(
        (state) => state.auth,
        shallowEqual
    )
    const { theme: appTheme } = useSelector((state) => state?.sideBar)
    const currentBuildVersionRef = useRef(null)
    const lastBuildVersionCheckAtRef = useRef(0)
    
    // Before redux-persist rehydrates, appTheme is null. applyTheme() ignores
    // that instead of writing data-theme="null", which would otherwise
    // overwrite the value the pre-paint script in public/index.html already
    // set on <html> and bring back the white flash for that window.
    const updateThemeAttribute = useCallback(() => {
        applyTheme(appTheme)
    }, [appTheme])
    
    useEffect(() => {
        updateThemeAttribute()
    }, [appTheme, updateThemeAttribute])

    const fetchBuildVersion = useCallback(async () => {
        try {
            const response = await fetch(
                `${BUILD_VERSION_ENDPOINT}?t=${Date.now()}`,
                {
                    cache: 'no-store',
                    headers: {
                        'Cache-Control': 'no-cache',
                        Pragma: 'no-cache',
                    },
                }
            )
            if (!response.ok) return null
            const payload = await response.json()
            const version = (payload?.version || '').trim()
            return version || null
        } catch {
            return null
        }
    }, [])

    useEffect(() => {
        const checkForNewBuild = async () => {
            const latestVersion = await fetchBuildVersion()
            if (!latestVersion) return

            if (!currentBuildVersionRef.current) {
                currentBuildVersionRef.current = latestVersion
                return
            }

            if (latestVersion === currentBuildVersionRef.current) return

            const lastReloadedVersion = sessionStorage.getItem(
                'last-auto-reloaded-build-version'
            )

            if (lastReloadedVersion === latestVersion) return

            sessionStorage.setItem(
                'last-auto-reloaded-build-version',
                latestVersion
            )
            window.location.reload()
        }

        const maybeCheckForNewBuild = (force = false) => {
            const now = Date.now()
            if (
                !force &&
                now - lastBuildVersionCheckAtRef.current <
                    BUILD_VERSION_MIN_CHECK_GAP_MS
            ) {
                return
            }
            lastBuildVersionCheckAtRef.current = now
            checkForNewBuild()
        }

        const handleFocus = () => {
            maybeCheckForNewBuild()
        }

        const handleVisibilityChange = () => {
            if (document.visibilityState === 'visible') {
                maybeCheckForNewBuild()
            }
        }

        maybeCheckForNewBuild(true)
        window.addEventListener('focus', handleFocus)
        document.addEventListener('visibilitychange', handleVisibilityChange)

        return () => {
            window.removeEventListener('focus', handleFocus)
            document.removeEventListener(
                'visibilitychange',
                handleVisibilityChange
            )
        }
    }, [fetchBuildVersion])



    useEffect(() => {
        const currentPath = location.pathname;
        const isLocalIncidentsPage = currentPath === '/incidents/localincidents' || 
                                   currentPath.startsWith('/incidents/editincident/');
        
       
        if (!isLocalIncidentsPage && !currentPath.startsWith('/incidents/')) {
            localStorage.removeItem('localIncidentsPaginationState');
        }
    }, [location.pathname])

    // Handle cross-tab logout events
    useEffect(() => {
        const cleanup = crossTabLogout.onLogout((logoutEvent) => {
            // Check if this is the same event we already processed
            if (lastLogoutEventRef.current === logoutEvent.eventId) {
                return;
            }
            
            lastLogoutEventRef.current = logoutEvent.eventId;
            
            // Show notification to user (only once per event)
            toast.info('You have been logged out due to logout in another tab', {
                position: "top-right",
                autoClose: 5000,
                hideProgressBar: false,
                closeOnClick: true,
                pauseOnHover: true,
                draggable: true,
                toastId: `logout-${logoutEvent.eventId}` // Prevent duplicate toasts
            })
            
            // Clear auth state
            dispatch(logout())
            
            // Redirect to login page if not already there
            if (!nonAuthRoutes.includes(location.pathname)) {
                navigate('/login', { replace: true })
            }
        })

        // Cleanup listener on unmount
        return cleanup
    }, [dispatch, navigate, location.pathname])

    return (
        <>
            <AuthInit>
                <Suspense fallback={<RotatingLinesLoader />}>
                    {isInitializing ? (
                        <RotatingLinesLoader />
                    ) : isAuthorized && token ? (
                        <PrivatePage />
                    ) : (
                        <PublicPage />
                    )}
                </Suspense>
            </AuthInit>
            <MaintenanceWatcher />
            <MaintenanceOverlay />
            <ToastContainer />
        </>
    )
}

export default App
